import axiosInstance from '../api/axiosInstance';
import { publicAxios } from '../api/axiosInstance';
import { API_ENDPOINTS } from '../api/config';

// Types
export interface GymCenter {
  id: number;
  name: string;
  type?: string;
  business_line?: string;
  description?: string;
  address?: string;
  city?: string;
  state?: string;
  phone?: string;
  email?: string;
  latitude?: number;
  longitude?: number;
  logo?: string;
  is_active: boolean;
}

export interface GymPackage {
  id: number;
  gym_center?: number;
  gym_center_name?: string;
  title: string;
  description?: string;
  duration_months: number;
  original_price: number;
  discounted_price: number;
  discount_percent?: number;
  features?: string[] | string;
  is_active: boolean;
  vendor_logo?: string;
  how_to_use?: string;
}

export interface GymVoucher {
  id: number;
  voucher_id: string;
  voucher_uuid: string;
  name: string;
  selected_package: string;
  package_title: string;
  status: 'pending' | 'active' | 'cancelled' | 'failed';
  amount_paid: number;
  booking_for: 'self' | 'dependant';
  start_date?: string;
  end_date?: string;
  payment_txn_id?: string;
  gym_center_name: string;
  gym_center_city: string;
  voucher_datetime: string;
}

export interface CreateGymVoucherPayload {
  gym_center_id: number;
  package_id: number;
  booking_for: 'self' | 'dependant';
  dependant_id?: number;
}

export interface GymPayUData {
  key: string;
  txnid: string;
  amount: string;
  productinfo: string;
  firstname: string;
  email: string;
  phone: string;
  surl: string;
  furl: string;
  hash: string;
  service_provider: string;
  payu_base_url: string;
}

export interface GymInitiatePaymentResponse {
  voucher_uuid: string;
  txn_id: string;
  payu_data: GymPayUData;
}

export interface InitiateGymPaymentPayload {
  package_id: number;
  gym_center_id: number;
  booking_for: 'self' | 'dependant';
  dependant_id?: number;
}

const gymService = {
  // Get all gym centers (public)
  getCenters: async (params?: { city?: string; state?: string }): Promise<GymCenter[]> => {
    const response = await publicAxios.get(API_ENDPOINTS.gym.centers, { params });
    const d = response.data; return Array.isArray(d) ? d : d.results || [];
  },

  // Get single gym center
  getCenter: async (id: number): Promise<GymCenter> => {
    const response = await publicAxios.get(`${API_ENDPOINTS.gym.centers}${id}/`);
    return response.data;
  },

  // Get all gym packages (public)
  getPackages: async (centerId?: number): Promise<GymPackage[]> => {
    const params = centerId ? { gym_center: centerId } : {};
    const response = await publicAxios.get(API_ENDPOINTS.gym.packages, { params });
    const d = response.data; return Array.isArray(d) ? d : d.results || [];
  },

  // Get single package
  getPackage: async (id: number): Promise<GymPackage> => {
    const response = await publicAxios.get(`${API_ENDPOINTS.gym.packages}${id}/`);
    return response.data;
  },

  // Get user's vouchers
  getVouchers: async (): Promise<GymVoucher[]> => {
    const response = await axiosInstance.get(API_ENDPOINTS.gym.vouchers);
    const d = response.data; return Array.isArray(d) ? d : d.results || [];
  },

  // Get single voucher
  getVoucher: async (id: number): Promise<GymVoucher> => {
    const response = await axiosInstance.get(`${API_ENDPOINTS.gym.vouchers}${id}/`);
    return response.data;
  },

  // Create voucher (book gym)
  createVoucher: async (data: CreateGymVoucherPayload): Promise<GymVoucher> => {
    const response = await axiosInstance.post(API_ENDPOINTS.gym.vouchers, data);
    return response.data;
  },

  // Activate voucher
  activateVoucher: async (id: number): Promise<GymVoucher> => {
    const response = await axiosInstance.post(`${API_ENDPOINTS.gym.vouchers}${id}/activate/`);
    return response.data;
  },

  // Cancel voucher
  cancelVoucher: async (id: number): Promise<GymVoucher> => {
    const response = await axiosInstance.post(`${API_ENDPOINTS.gym.vouchers}${id}/cancel/`);
    return response.data;
  },

  // Initiate payment — creates voucher + returns PayU form data
  initiatePayment: async (data: InitiateGymPaymentPayload): Promise<GymInitiatePaymentResponse> => {
    const response = await axiosInstance.post(API_ENDPOINTS.gym.initiatePayment, data);
    return response.data;
  },

  resumePayment: async (voucherUuid: string): Promise<GymInitiatePaymentResponse> => {
    const response = await axiosInstance.post(API_ENDPOINTS.gym.resumePayment, {
      voucher_uuid: voucherUuid,
    });
    return response.data;
  },
};

export default gymService;
